import { useState, type FormEvent } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { api, errorMessage } from '@/lib/api';
import { homePath, useMe, useSetMe } from '@/lib/auth';
import type { Me } from '@/lib/types';
import { Button } from '@/components/ui/button';
import { Field, Input } from '@/components/ui/field';
import { Alert } from '@/components/ui/misc';
import { AuthLayout } from './AuthLayout';

/** Only same-app paths are honoured as a post-sign-in destination. */
export function safeNext(next: string | null) {
  return next && next.startsWith('/') && !next.startsWith('//') ? next : null;
}

export function routeAfterAuth(me: Me, next: string | null) {
  if (me.mfa === 'verify') return '/mfa' + (next ? `?next=${encodeURIComponent(next)}` : '');
  if (me.mfa === 'enroll') return '/mfa/setup';
  return safeNext(next) ?? homePath(me);
}

export function SignIn() {
  const { data: me } = useMe();
  const setMe = useSetMe();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (me && me.mfa === 'ok') return <Navigate to={safeNext(params.get('next')) ?? homePath(me)} replace />;

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await api.post<Me>('/api/auth/login', { email, password });
      setMe(res);
      nav(routeAfterAuth(res, params.get('next')), { replace: true });
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  }

  return (
    <AuthLayout title="Sign in" subtitle="Accounts are by invitation only.">
      <form onSubmit={submit} className="flex flex-col gap-4">
        {error && <Alert tone="bad">{error}</Alert>}
        <Field label="Email" htmlFor="email">
          <Input
            id="email"
            type="email"
            autoComplete="username"
            required
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </Field>
        <Field label="Password" htmlFor="password">
          <Input
            id="password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </Field>
        <Button type="submit" size="lg" loading={busy} className="mt-1">
          Sign in
        </Button>
      </form>
    </AuthLayout>
  );
}
